import { UNI_ROLES, UserRole } from '../store/auth/types';
import { IStudentRow } from 'types';

import isemail from 'isemail';
import {
  parseDate,
  addToDate,
  subtractFromDate,
  isBefore,
  isAfter,
  isSameOrAfter,
  isSameOrBefore,
  now,
} from 'shared-components';
import { Temporal } from '@js-temporal/polyfill';
import { ICourse, IDate, IImportCourse } from '../types';
import { normalizeToFullYear } from './formatting';

export const selectedStudentValidation = (
  userRole: UserRole,
  isPathwayProvider: boolean,
  selectedRows: string[],
  visibleStudents: IStudentRow[],
): boolean => {
  // If only one student has been selected
  const oneRecordSelected = selectedRows.length === 1;
  const selectedStudents = visibleStudents?.filter(value => selectedRows.indexOf(value.p_key) > -1) ?? [];

  // If user is Direct Uni, do not allow any options for pathway students.
  const isDirectUniUser = !isPathwayProvider && UNI_ROLES.includes(userRole);
  const isPathwayStudent = !!selectedStudents[0]?.pathway;
  const directUniUserCondition = isDirectUniUser ? !isPathwayStudent : true;

  return oneRecordSelected && directUniUserCondition;
};

export const isPassportExpired = (passportExpiry: string): boolean => {
  const expiryDate = parseDate(passportExpiry);
  if (!expiryDate) return false;
  return isSameOrAfter(now(), expiryDate);
};

export const isPassportExpiringSoon = (courses: Array<ICourse>, passportExpiry: string): boolean => {
  const expiryDate = parseDate(passportExpiry);
  if (!expiryDate) return false;
  const startDate = getEarliestCourseStartDate(courses);
  if (startDate) {
    const sixMonthsLater = addToDate(startDate, { months: 6 });
    return sixMonthsLater ? isAfter(sixMonthsLater, expiryDate) : false;
  }
  return false;
};

export function getEarliestCourseStartDate(courses: Array<ICourse> | Array<IImportCourse>): Temporal.PlainDate | null {
  if (courses.length === 0) return null;
  let winningDate: Temporal.PlainDate | null = null;
  for (let i = 0; i < courses.length; i++) {
    const course = courses[i];
    if (!course || !course.startDate) {
      continue;
    }
    const challengerDate = parseDate(course.startDate);
    if (challengerDate && (winningDate === null || isBefore(challengerDate, winningDate))) {
      winningDate = challengerDate;
    }
  }
  return winningDate;
}

export const getMaturityDate = (userDate?: string): Temporal.PlainDate | null => {
  if (userDate) {
    const date = parseDate(userDate);
    return date ? addToDate(date, { days: 28 }) : null;
  }
  return subtractFromDate(now(), { days: 28 });
};

export const validateStudentNo = (studentNumber: string, regexStr: string, errorText: string): string => {
  if (studentNumber.trim() === '') {
    return 'Student Number can not be empty';
  }
  if (/[^a-zA-Z0-9\-/]/.test(studentNumber)) {
    return 'Student Number can not contain special characters';
  }
  const regex = regexStr ? new RegExp(regexStr) : null;
  if (regex && !regex.test(studentNumber)) {
    return errorText;
  }
  return '';
};

export const validateEmail = (email: string): string => {
  if (email.trim() === '') {
    return 'Email can not be empty';
  }

  if (!isemail.validate(email)) {
    return 'Email is not valid';
  }
  return '';
};

export const getTemporalDate = (dateInput: IDate): Temporal.PlainDate | null => {
  const { YYYY, DD, MM } = dateInput;
  const year = normalizeToFullYear(YYYY);
  // Create ISO date string from the components
  const isoDate = `${year}-${String(MM).padStart(2, '0')}-${String(DD).padStart(2, '0')}`;
  return parseDate(isoDate);
};

export const getTemporalDateOrNull = (dateInput: IDate): Temporal.PlainDate | null => {
  const { YYYY, DD, MM } = dateInput;
  if (YYYY && DD && MM) {
    return getTemporalDate(dateInput);
  }
  return null;
};

export const isFutureDate = (dateInput: IDate): boolean => {
  const futureDate = getTemporalDateOrNull(dateInput);
  if (futureDate) return isSameOrAfter(futureDate, now());
  return false;
};

export const isPastDate = (dateInput: IDate): boolean => {
  const pastDate = getTemporalDateOrNull(dateInput);
  if (pastDate) return isSameOrBefore(pastDate, now());
  return false;
};
